import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { usePriceLive } from '@ui/hooks/price/usePriceLive';
import { useTradePairInfos } from '@ui/hooks/trade/useTradePairInfos';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { getLeverageMargin } from '@ui/utils/trade/getLeverageMargin';
import { useMemo } from 'react';

import { useLeverageChange } from './useLeverageChange';

export const useLiquidationPriceChange = ({
  position,
}: {
  position: Position;
}) => {
  const price = usePayTokenPriceLatest(position.collateralToken);
  const pairPrice = usePriceLive(position.pair.baseSymbol);
  const leverageChange = useLeverageChange({ position });
  const pairInfos = useTradePairInfos();

  const pairInfo = useMemo(() => {
    return pairInfos?.find(
      (v) => v.address.toLowerCase() === position.pair.address.toLowerCase(),
    );
  }, [pairInfos, position.pair.address]);

  return useMemo(() => {
    if (!price || !pairPrice || !leverageChange || !pairInfo?.leverageMargins)
      return;

    const sizeUsd = position.getSizeUsd(pairPrice);
    const prevLiqLostP = getLeverageMargin({
      leverage: leverageChange.prev.toNumber(),
      leverageMargins: pairInfo.leverageMargins,
    })?.liqLostP;
    const nextLiqLostP = getLeverageMargin({
      leverage: leverageChange.next.toNumber(),
      leverageMargins: pairInfo.leverageMargins,
    })?.liqLostP;
    if (!prevLiqLostP || !nextLiqLostP) return;

    const countableCollateralUsdPrev = sizeUsd.div(leverageChange.prev);
    const countableCollateralUsdNext = sizeUsd.div(leverageChange.next);
    const prevLiquidationPrice = pairInfo.feeConfig
      ? position.getLiquidationPrice({
          collateralMarketPrice: price,
          pairMarketPrice: pairPrice,
          feeConfig: pairInfo.feeConfig,
          liqLostP: prevLiqLostP,
          tradePair: pairInfo,
          override: {
            collateralUsd: countableCollateralUsdPrev,
          },
        })
      : undefined;
    const nextLiquidationPrice = pairInfo.feeConfig
      ? position.getLiquidationPrice({
          collateralMarketPrice: price,
          pairMarketPrice: pairPrice,
          feeConfig: pairInfo.feeConfig,
          liqLostP: nextLiqLostP,
          tradePair: pairInfo,
          override: {
            collateralUsd: countableCollateralUsdNext,
          },
        })
      : undefined;

    return {
      prev: prevLiquidationPrice,
      next: nextLiquidationPrice,
    };
  }, [leverageChange, pairInfo, pairPrice, position, price]);
};
